import type { FC } from 'react'

import clsx from 'clsx'

import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Skeleton, SkeletonTheme } from 'shared/ui/skeleton'

import { RestPlaceCardItem } from '../rest-places'

import s from './styles.module.scss'

interface RestPlacesHorizontalLayoutProps {
  places?: RestPlaceCardItem[]
  isLoading?: boolean
  onSelectPlace?: (place: RestPlaceCardItem) => void
}

type PlaceItem = NonNullable<RestPlacesHorizontalLayoutProps['places']>[number]

const renderPlaceCard = (
  place: PlaceItem,
  onSelectPlace?: (place: PlaceItem) => void,
  className?: string,
) => (
  <button
    key={place.id}
    type="button"
    className={clsx(s.card, className)}
    onClick={() => onSelectPlace?.(place)}
    aria-label={place.title}
  >
    {place.image ? (
      <img
        src={place.image}
        alt={place.title}
        className={s.image}
        loading="lazy"
      />
    ) : (
      <div className={s.placeholder}>
        <AppIcon
          id={AllIcons.PIN}
          width={24}
          height={24}
        />
      </div>
    )}

    <p className={s.title}>{place.title}</p>
  </button>
)

const RestPlacesHorizontalSkeleton = () => (
  <SkeletonTheme
    baseColor="var(--grey-32)"
    highlightColor="var(--grey-33)"
  >
    <div className={s.container}>
      <div className={s.skeletonBigCard}>
        <Skeleton className={s.skeletonBigImage} />
      </div>

      <div className={s.skeletonSmallGrid}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className={s.skeletonSmallCard}
          >
            <Skeleton className={s.skeletonSmallImage} />
          </div>
        ))}
      </div>
    </div>
  </SkeletonTheme>
)

export const RestPlacesHorizontalLayout: FC<
  RestPlacesHorizontalLayoutProps
> = ({ places, isLoading = false, onSelectPlace }) => {
  const preparedPlaces = places ?? []
  const firstPlace = preparedPlaces[0]
  const smallPlaces = preparedPlaces.slice(1)

  if (isLoading) {
    return <RestPlacesHorizontalSkeleton />
  }

  return (
    <div className={s.container}>
      {firstPlace
        ? renderPlaceCard(firstPlace, onSelectPlace, s.bigCard)
        : null}

      {smallPlaces.length > 0 && (
        <div className={s.smallGrid}>
          {smallPlaces.map((place) =>
            renderPlaceCard(place, onSelectPlace, s.smallCard),
          )}
        </div>
      )}
    </div>
  )
}
